import { useState, useEffect } from 'react';
import { AlertOctagon, Plus, MessageSquare, Clock, CheckCircle, Eye } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { ReportProblemModal } from './ReportProblemModal';

interface Ticket {
  id: string;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high';
  category: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  created_at: string;
  updated_at: string;
  admin_response?: string;
}

export function MyTicketsView() {
  const { profile } = useAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyTickets();
  }, [profile]);

  const fetchMyTickets = async () => {
    if (!profile?.id) return;

    setLoading(true);
    const { data, error } = await supabase
      .from('support_tickets')
      .select('*')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tickets:', error);
    } else {
      setTickets(data || []);
    }
    setLoading(false);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'text-blue-600 bg-blue-100';
      case 'in_progress': return 'text-yellow-600 bg-yellow-100';
      case 'resolved': return 'text-green-600 bg-green-100';
      case 'closed': return 'text-slate-600 bg-slate-100';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return Clock;
      case 'in_progress': return MessageSquare;
      case 'resolved': return CheckCircle;
      case 'closed': return CheckCircle;
      default: return Clock;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-3 rounded-lg">
              <AlertOctagon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">My Support Tickets</h2>
              <p className="text-slate-600">View your submitted tickets and responses</p>
            </div>
          </div>
          <button
            onClick={() => setShowReportModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            <Plus className="w-5 h-5" />
            Report Problem
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-slate-600 mt-4">Loading your tickets...</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12">
            <AlertOctagon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Tickets Yet</h3>
            <p className="text-slate-600 mb-6">You haven't submitted any support tickets.</p>
            <button
              onClick={() => setShowReportModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              <Plus className="w-5 h-5" />
              Report Your First Problem
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Tickets List */}
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {tickets.map((ticket) => {
                const StatusIcon = getStatusIcon(ticket.status);
                return (
                  <div
                    key={ticket.id}
                    onClick={() => setSelectedTicket(ticket)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedTicket?.id === ticket.id
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 mb-1">
                          #{ticket.id.slice(0, 8)} - {ticket.title}
                        </h3>
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority.toUpperCase()}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1 ${getStatusColor(ticket.status)}`}>
                          <StatusIcon className="w-3 h-3" />
                          {ticket.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 line-clamp-2 mb-2">{ticket.description}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span>{ticket.category}</span>
                      <span>•</span>
                      <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                      {ticket.admin_response && (
                        <>
                          <span>•</span>
                          <span className="text-green-600 font-semibold flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            Response received
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Ticket Detail */}
            <div className="border-2 border-slate-200 rounded-lg p-6 max-h-[600px] overflow-y-auto">
              {selectedTicket ? (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      Ticket #{selectedTicket.id.slice(0, 8)}
                    </h3>
                    <div className="flex gap-2 mb-4">
                      <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getPriorityColor(selectedTicket.priority)}`}>
                        {selectedTicket.priority.toUpperCase()}
                      </span>
                      <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getStatusColor(selectedTicket.status)}`}>
                        {selectedTicket.status.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className="text-xs px-3 py-1 rounded-full font-semibold bg-slate-100 text-slate-700">
                        {selectedTicket.category}
                      </span>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Problem Title</h4>
                    <p className="text-slate-700">{selectedTicket.title}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Description</h4>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedTicket.description}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Submitted</h4>
                    <p className="text-sm text-slate-700">
                      {new Date(selectedTicket.created_at).toLocaleString()}
                    </p>
                  </div>

                  {selectedTicket.admin_response ? (
                    <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <MessageSquare className="w-5 h-5 text-green-600" />
                        <h4 className="font-semibold text-green-900">Support Team Response</h4>
                      </div>
                      <p className="text-sm text-green-800 whitespace-pre-wrap">{selectedTicket.admin_response}</p>
                      <p className="text-xs text-green-600 mt-3">
                        Updated: {new Date(selectedTicket.updated_at).toLocaleString()}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-5 h-5 text-blue-600" />
                        <h4 className="font-semibold text-blue-900">Waiting for Response</h4>
                      </div>
                      <p className="text-sm text-blue-800">
                        Our support team will review your ticket and respond as soon as possible.
                      </p>
                    </div>
                  )}

                  {selectedTicket.status === 'resolved' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <p className="text-sm text-green-800 font-semibold">
                          This ticket has been marked as resolved.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Eye className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Select a ticket to view details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Report Problem Modal */}
      {showReportModal && (
        <ReportProblemModal
          onClose={() => {
            setShowReportModal(false);
            fetchMyTickets(); // Refresh tickets after submitting
          }}
        />
      )}
    </div>
  );
}
